import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useChatState } from '@core/chat';
import { useModalCTAContext } from '@providers/ModalCTAProvider';
import { useLogout } from '@components/ConfirmLogoutModal/hooks';
import ModalCountDown from '@core/dashboard/components/ModalCountDown';
import { getClientSpecificMandatoryLogoutModal } from '@helpers/clientSpecificUtils';

const getUtcTime = (secondsToAdd: number) => {
  const now = new Date();
  return Math.floor(now.getTime() / 1000) + secondsToAdd;
};

const useMandatoryLogoutModal = () => {
  const { t: translate } = useTranslation();

  const { cleanStates, events } = useMytaverseEvent();
  const { resetState } = useChatState();
  const { setValuesForModal } = useModalCTAContext();

  const [isOpen, setIsOpen] = useState(false);

  const handleOnConfirmLogout = useCallback(() => {
    cleanStates();
    resetState();
  }, [cleanStates, resetState]);

  const handleExitButtonClick = useLogout({ onConfirm: handleOnConfirmLogout });
  const { onCancel } = getClientSpecificMandatoryLogoutModal(events);

  return useCallback(
    (time = 30, message = '') => {
      setIsOpen(true);

      setValuesForModal({
        open: !isOpen,
        title: translate('mandatoryLogout.title'),
        message: message || translate('mandatoryLogout.message1', { time }),
        altStyling: true,
        onConfirm: handleExitButtonClick,
        onCancelAltAction: onCancel,
        onCancel: () => {
          setIsOpen(false);
        },
        cancelButtonName: translate('mandatoryLogout.returnToHub'),
        confirmButtonName: translate('userProfile.logOut'),
        children: (
          <ModalCountDown time={getUtcTime(time)} onFinish={onCancel} />
        ),
      });

      setTimeout(() => {
        onCancel();
      }, time * 1000);
    },
    [handleExitButtonClick, isOpen],
  );
};

export default useMandatoryLogoutModal;
